import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

// Public Pages
import HomePage from "./pages/HomePage";
import RoomsPage from "./pages/RoomsPage";
import AboutPage from "./pages/AboutPage";
import StoryPage from "./pages/StoryPage";
import LondonPage from "./pages/LondonPage";
import RetreatOnboarding from "./pages/RetreatOnboarding";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import FeedbackPage from "./pages/FeedbackPage";
import GoogleAuthCallback from "./components/GoogleAuthCallback";import StudioPage from "./pages/StudioPage";
import OffsitePage from "./pages/OffsitePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AreYouSaviPage from "./pages/AreYouSaviPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import DeparturePage from "./pages/DeparturePage";
import HomeV2Page from "./pages/HomeV2Page";

// SEO Landing Pages (not in nav)
import ColivingValenciaPage from "./pages/seo/ColivingValenciaPage";
import QuietColivingValenciaPage from "./pages/seo/QuietColivingValenciaPage";
import LuxuryColivingValenciaPage from "./pages/seo/LuxuryColivingValenciaPage";
import ColivingFoundersPage from "./pages/seo/ColivingFoundersPage";
import CoworkingValenciaPage from "./pages/seo/CoworkingValenciaPage";
import SoberColivingPage from "./pages/seo/SoberColivingPage";
import WhyMostColivingsDontWork from "./pages/seo/WhyMostColivingsDontWork";
import SoberLivingRemoteWorkers from "./pages/seo/SoberLivingRemoteWorkers";
import BestSoberColivingEurope from "./pages/seo/BestSoberColivingEurope";
import BestColivingSpain from "./pages/seo/BestColivingSpain";
import IsSoberColivingWorthIt from "./pages/seo/IsSoberColivingWorthIt";
import ColivingVsRetreatVsSober from "./pages/seo/ColivingVsRetreatVsSober";
import SoberCuriousGuide from "./pages/seo/SoberCuriousGuide";
import DigitalNomadColivingSpain from "./pages/seo/DigitalNomadColivingSpain";
import AlcoholFreeTravel from "./pages/seo/AlcoholFreeTravel";
import LivingAbroadWithoutDrinking from "./pages/seo/LivingAbroadWithoutDrinking";
import ColivingForEntrepreneurs from "./pages/seo/ColivingForEntrepreneurs";
import RemoteWorkerCommunity from "./pages/seo/RemoteWorkerCommunity";
import IntentionalLiving from "./pages/seo/IntentionalLiving";
import DeepWorkEnvironment from "./pages/seo/DeepWorkEnvironment";

// Components
import WhatsAppButton from "./components/WhatsAppButton";
import CookieConsent from "./components/CookieConsent";
import { ForcePasswordChange } from "./components/ForcePasswordChange";

// Protected Pages (Guest Portal)
import OnboardingWizard from "./pages/OnboardingWizard";
import GuestDashboard from "./pages/GuestDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSojournsPage from "./pages/AdminSojournsPage";
import SojournsPage from "./pages/SojournsPage";
import SojournsNotePage from "./pages/SojournsNotePage";
import SojournsNotesPage from "./pages/SojournsNotesPage";
import HandbookPage from "./pages/HandbookPage";
import CommunityPage from "./pages/CommunityPage";
import CompatibilityPage from "./pages/CompatibilityPage";
import ArrivalPage from "./pages/ArrivalPage";
import AreaGuidePage from "./pages/AreaGuidePage";
import SaviLibraryPage from "./pages/SaviLibraryPage";
import VolunteerSchedulePage from "./pages/VolunteerSchedulePage";
import CareersPage from "./pages/CareersPage";
import HalloweenCritters from "./components/HalloweenCritters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// PWA manifest is only attached on portal pages — keeps the browser's
// "install app" prompt off the public marketing site.
const PORTAL_PREFIXES = ["/login", "/onboarding", "/retreat-welcome", "/dashboard", "/admin", "/handbook", "/community", "/compatibility", "/arrival", "/guide", "/library"];
const PortalManifest = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPortal = PORTAL_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
    let link = document.querySelector('link[rel="manifest"]');
    if (isPortal && !link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/manifest.json";
      document.head.appendChild(link);
    } else if (!isPortal && link) {
      link.remove();
    }
  }, [pathname]);

  return null;
};

// savisojourns.com is a pointer domain: send it straight to the Sojourns page.
if (typeof window !== "undefined" && /(^|\.)savisojourns\.com$/i.test(window.location.hostname)) {
  window.location.replace(`https://www.savicoliving.com/sojourns${window.location.search}`);
}

// Season theme for the resident app: Admin → Settings picks it; applied as a class on <html>
// only on portal routes so the public site keeps the brand colours. Cached so there's no flash.
const PortalTheme = () => {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("savi_theme") || "classic");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/app-theme`)
      .then((res) => {
        const next = res.data?.effective || "classic";
        if (!cancelled) { setTheme(next); localStorage.setItem("savi_theme", next); }
      })
      .catch(() => {});
    const onChange = (e) => { const next = e.detail || "classic"; setTheme(next); localStorage.setItem("savi_theme", next); };
    const onRefresh = () => axios.get(`${API}/app-theme`).then((res) => onChange({ detail: res.data?.effective })).catch(() => {});
    window.addEventListener("savi-theme-changed", onChange);
    window.addEventListener("savi-theme-refresh", onRefresh);
    return () => { cancelled = true; window.removeEventListener("savi-theme-changed", onChange); window.removeEventListener("savi-theme-refresh", onRefresh); };
  }, [pathname]);

  const isPortal = PORTAL_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  useEffect(() => {
    const root = document.documentElement;
    [...root.classList].filter((c) => c.startsWith("theme-")).forEach((c) => root.classList.remove(c));
    if (isPortal && theme && theme !== "classic") root.classList.add(`theme-${theme}`);
    root.dataset.theme = isPortal ? theme : "classic";
  }, [isPortal, theme]);

  return isPortal && theme === "halloween" ? <HalloweenCritters /> : null;
};
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Token storage: localStorage when "Remember me" is on (persists across sessions),
// sessionStorage when off (cleared when the browser/tab closes).
const getStoredToken = () => localStorage.getItem("savi_token") || sessionStorage.getItem("savi_token");
const storeToken = (t, remember) => {
  if (remember) {
    localStorage.setItem("savi_token", t);
    sessionStorage.removeItem("savi_token");
  } else {
    sessionStorage.setItem("savi_token", t);
    localStorage.removeItem("savi_token");
  }
};
// On silent refresh we don't know the remember choice — keep writing to whichever store already holds it.
const refreshStoredToken = (t) => {
  if (sessionStorage.getItem("savi_token") && !localStorage.getItem("savi_token")) {
    sessionStorage.setItem("savi_token", t);
  } else {
    localStorage.setItem("savi_token", t);
  }
};
const clearStoredToken = () => {
  localStorage.removeItem("savi_token");
  sessionStorage.removeItem("savi_token");
};

// Cache the last known user so a transient server error (e.g. a pod restart) doesn't
// blank the session and bounce the user to /login. Password is never persisted.
const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("savi_user") || sessionStorage.getItem("savi_user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const storeUser = (u) => {
  if (!u) return;
  const { password, ...safe } = u;
  const raw = JSON.stringify(safe);
  if (sessionStorage.getItem("savi_token") && !localStorage.getItem("savi_token")) {
    sessionStorage.setItem("savi_user", raw);
  } else {
    localStorage.setItem("savi_user", raw);
  }
};
const clearStoredUser = () => {
  localStorage.removeItem("savi_user");
  sessionStorage.removeItem("savi_user");
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredToken() ? getStoredUser() : null);
  const [loading, setLoading] = useState(getStoredToken() ? !getStoredUser() : false);
  const [token, setToken] = useState(getStoredToken());

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    const maxAttempts = 4;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        storeUser(response.data);
        // Sliding session: silently swap in a fresh long-lived token so active users never get logged out
        try {
          const r = await axios.post(`${API}/auth/refresh`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (r.data?.token) refreshStoredToken(r.data.token);
        } catch { /* keep current token */ }
        setLoading(false);
        return;
      } catch (error) {
        const status = error?.response?.status;
        // Only log out when the server EXPLICITLY rejects the token.
        if (status === 401 || status === 403) {
          logout();
          setLoading(false);
          return;
        }
        // Transient failure (offline, 5xx, pod restart/OOM): retry with backoff before giving up.
        if (attempt < maxAttempts) {
          await new Promise((res) => setTimeout(res, 1500 * attempt));
          continue;
        }
        // Still failing after retries — keep the cached session so a server hiccup
        // never bounces a logged-in user to the login screen.
        console.error("Auth revalidation failed (keeping cached session):", error);
        const cached = getStoredUser();
        if (cached) setUser(cached);
        setLoading(false);
        return;
      }
    }
  };

  const login = async (email, password, remember = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, is_first_login } = response.data;
    storeToken(newToken, remember);
    if (is_first_login) {
      localStorage.setItem("savi_first_login", "true");
    }
    setToken(newToken);
    setUser(userData);
    storeUser(userData);
    return { ...userData, is_first_login };
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    storeToken(newToken, true);
    setToken(newToken);
    setUser(userData);
    storeUser(userData);
    return userData;
  };

  const logout = () => {
    clearStoredToken();
    clearStoredUser();
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => {
      const next = { ...prev, ...updates };
      storeUser(next);
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-pulse-soft">
          <div className="w-16 h-16 rounded-full bg-[#E07A5F]/20 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-[#E07A5F]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  // Emergent Google Auth redirect lands with #session_id=... — handle it before any routing
  if (window.location.hash.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }

  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PortalManifest />
        <PortalTheme />
        <ForcePasswordChange />
        <div className="min-h-screen bg-[#FDFCF8]">
          <Routes>
            {/* Public Website Pages */}
            <Route path="/" element={<><HomePage /><WhatsAppButton /></>} />
            <Route path="/v2" element={<><HomeV2Page /><WhatsAppButton /></>} />
            <Route path="/rooms" element={<><RoomsPage /><WhatsAppButton /></>} />
            <Route path="/about" element={<><AboutPage /><WhatsAppButton /></>} />
            <Route path="/story" element={<><StoryPage /><WhatsAppButton /></>} />
            <Route path="/london" element={<><LondonPage /><WhatsAppButton /></>} />
            <Route path="/faq" element={<><FAQPage /><WhatsAppButton /></>} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/sojourns" element={<SojournsPage />} />
            <Route path="/sojourns/notes" element={<SojournsNotesPage />} />
            <Route path="/sojourns/:slug" element={<SojournsNotePage />} />
            <Route path="/contact" element={<><ContactPage /><WhatsAppButton /></>} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/studio" element={<><StudioPage /><WhatsAppButton /></>} />
            <Route path="/offsites" element={<><OffsitePage /><WhatsAppButton /></>} />
            <Route path="/offsite" element={<Navigate to="/offsites" replace />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/are-you-savi" element={<AreYouSaviPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/departure" element={<DeparturePage />} />
            <Route path="/coliving-valencia" element={<><ColivingValenciaPage /><WhatsAppButton /></>} />
            <Route path="/quiet-coliving-valencia" element={<><QuietColivingValenciaPage /><WhatsAppButton /></>} />
            <Route path="/luxury-coliving-valencia" element={<><LuxuryColivingValenciaPage /><WhatsAppButton /></>} />
            <Route path="/coliving-valencia-for-founders" element={<><ColivingFoundersPage /><WhatsAppButton /></>} />
            <Route path="/coworking-valencia" element={<><CoworkingValenciaPage /><WhatsAppButton /></>} />
            <Route path="/sober-coliving" element={<><SoberColivingPage /><WhatsAppButton /></>} />
            <Route path="/why-most-colivings-dont-work" element={<WhyMostColivingsDontWork />} />
            <Route path="/sober-living-remote-workers" element={<SoberLivingRemoteWorkers />} />
            <Route path="/best-sober-coliving-europe" element={<BestSoberColivingEurope />} />
            <Route path="/best-coliving-spain" element={<BestColivingSpain />} />
            <Route path="/is-sober-coliving-worth-it" element={<IsSoberColivingWorthIt />} />
            <Route path="/coliving-vs-retreat-vs-sober-coliving" element={<ColivingVsRetreatVsSober />} />
            <Route path="/sober-curious-guide" element={<SoberCuriousGuide />} />
            <Route path="/digital-nomad-coliving-spain" element={<DigitalNomadColivingSpain />} />
            <Route path="/alcohol-free-travel" element={<AlcoholFreeTravel />} />
            <Route path="/living-abroad-without-drinking" element={<LivingAbroadWithoutDrinking />} />
            <Route path="/coliving-for-entrepreneurs" element={<ColivingForEntrepreneurs />} />
            <Route path="/how-to-find-community-as-remote-worker" element={<RemoteWorkerCommunity />} />
            <Route path="/what-is-intentional-living" element={<IntentionalLiving />} />
            <Route path="/deep-work-environment" element={<DeepWorkEnvironment />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            
            {/* Protected Guest Portal Pages */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/retreat-welcome"
              element={
                <ProtectedRoute>
                  <RetreatOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <GuestDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sojourns"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSojournsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/handbook"
              element={
                <ProtectedRoute>
                  <HandbookPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <CommunityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compatibility"
              element={
                <ProtectedRoute>
                  <CompatibilityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/arrival"
              element={
                <ProtectedRoute>
                  <ArrivalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guide"
              element={
                <ProtectedRoute>
                  <AreaGuidePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <SaviLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volunteer-schedule/:weekStart"
              element={
                <ProtectedRoute>
                  <VolunteerSchedulePage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <CookieConsent />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
